import React, { useCallback, useEffect, useLayoutEffect, useState, useContext } from 'react';
import type { FC } from 'react';

import type { EditorActions } from '@atlaskit/editor-core';
import type { InlineCommentViewComponentProps } from '@atlaskit/editor-plugins/annotation';

import { getEditorAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getEditorAnnotationEventEmitter.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import type { CommentAction } from '@confluence/inline-comments-common/entry-points/inlineCommentsTypes.ts';
import { useAnnotationsDispatch } from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';
import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';
import {
	handleCommentsPanelNavigationWithViewChange,
	ScrollBehavior,
	NavigationContext,
} from '@confluence/comments-util/entry-points/commentsNavigationUtils.ts';
import { useShowCommentsPanel } from '@confluence/comments-panel/entry-points/useShowCommentsPanel.ts';
import {
	useCommentsPanelActions,
	useCommentsPanelState,
	ViewValues,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { VIEW_INLINE_COMMENT_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { useOmniBarVisibilityActions } from '@confluence/omni-bar-api/entry-points/OmniBarVisibilityStore.ts';

import { InlineComment } from '../InlineComment.tsx';
import { LoadableActivatable } from '@confluence/loadable';

// Loaded on demand — only rendered when user clicks a reaction annotation.
const NOOP = () => {};

const InlineReaction = LoadableActivatable({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-InlineReaction" */ '../InlineReaction.tsx'))
			.InlineReaction,
});

export type ViewComponentProps = {
	pageId: string;
	editorActions?: EditorActions;
	reactionMarkerMap?: Record<string, string>;
};

export const editorHandleNavigationClick = (nextMarkerRef: string) => {
	const emitter = getEditorAnnotationEventEmitter();
	emitter.emit('setselectedannotation', nextMarkerRef);
};

export const ViewComponent: FC<InlineCommentViewComponentProps & ViewComponentProps> = ({
	annotations: clickedAnnotations,
	getInlineNodeTypes,
	pageId,
	editorActions,
	onClose,
	onDelete,
	isOpeningMediaCommentFromToolbar = false,
	isOffline,
	reactionMarkerMap = {},
}) => {
	const { setEditorViewProps } = useAnnotationsDispatch();
	const isLivePage = useIsCurrentPageLive();

	const supportedCommentActions: CommentAction[] = !isLivePage
		? ['edit', 'resolve']
		: ['edit', 'resolve', 'delete'];

	const { setCurrentlySelectedThreadKey, setCurrentView } = useCommentsPanelActions();
	const { currentView } = useCommentsPanelState();
	const { isCommentsPanelShown } = useShowCommentsPanel();
	const experienceTracker = useContext(ExperienceTrackerContext);

	// Hide OmniBar while a comment is selected (standalone or in panel). Only release when comment is fully closed.
	const { requestHideOmniBar, releaseHideOmniBar } = useOmniBarVisibilityActions();
	useLayoutEffect(() => {
		if (clickedAnnotations.length > 0 && fg('cc-engagement-omnibar-hide-inline-comments')) {
			requestHideOmniBar();
			return () => releaseHideOmniBar();
		}
	}, [clickedAnnotations.length, requestHideOmniBar, releaseHideOmniBar]);

	const [isInlineCommentVisible, setIsInlineCommentVisible] = useState<boolean>(false);
	const inlineCommentRef = useCallback((node: any) => {
		if (node === null) {
			setIsInlineCommentVisible(false);
		} else {
			setIsInlineCommentVisible(true);
		}
	}, []);

	const handleResolve = () => {
		setEditorViewProps(null);
	};

	const handleDelete = useCallback(
		(id: string) => {
			setEditorViewProps(null);
			onDelete && onDelete(id);
		},
		[setEditorViewProps, onDelete],
	);

	useEffect(() => {
		const annotationId = clickedAnnotations[0]?.id;
		if (!annotationId) return;

		const shouldScrollCommentsPanelToComment =
			isCommentsPanelShown && clickedAnnotations.length > 0;
		if (shouldScrollCommentsPanelToComment) {
			setCurrentlySelectedThreadKey(annotationId);
			let expectedView = undefined;
			if (currentView === ViewValues.RESOLVED || currentView === ViewValues.DELETED_CONTENT) {
				setCurrentView && setCurrentView(ViewValues.OPEN);
				expectedView = ViewValues.OPEN;
			}
			void handleCommentsPanelNavigationWithViewChange({
				scrollBehavior: ScrollBehavior.SMOOTH,
				context: NavigationContext.PANEL,
				block: 'center',
				annotationId,
				commentType: 'INLINE',
				annotationClicked: true,
				expectedView,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [clickedAnnotations[0]?.id]);

	const handleNavigationClick = (nextMarkerRef: string) => {
		experienceTracker.updateAttributes({
			name: VIEW_INLINE_COMMENT_EXPERIENCE,
			attributes: {
				annotationId: nextMarkerRef,
			},
		});
		editorHandleNavigationClick(nextMarkerRef);
	};

	const shouldRenderStandaloneInlineComment =
		clickedAnnotations.length > 0 && !isCommentsPanelShown;

	if (!shouldRenderStandaloneInlineComment) {
		return <></>;
	}

	const selectedAnnotation = clickedAnnotations[0];
	const isReactionAnnotation =
		selectedAnnotation.id in reactionMarkerMap &&
		expVal('confluence_inline_reactions', 'isEnabled', false);
	const reactionCommentId = isReactionAnnotation ? reactionMarkerMap[selectedAnnotation.id] : '';

	if (isReactionAnnotation) {
		return (
			<InlineReaction
				isEditor
				pageId={pageId}
				annotation={selectedAnnotation}
				commentId={reactionCommentId}
				onClose={onClose ?? NOOP}
				onNavigationClick={(nextMarkerRef: string) => handleNavigationClick(nextMarkerRef)}
				onResolve={handleResolve}
				inlineCommentRef={inlineCommentRef}
				key={`inline-reaction-${selectedAnnotation.id}`}
			/>
		);
	}

	return (
		<InlineComment
			isEditor
			annotation={selectedAnnotation}
			getInlineNodeTypes={getInlineNodeTypes}
			onClose={onClose}
			editorActions={editorActions}
			onNavigationClick={(nextMarkerRef: string) => handleNavigationClick(nextMarkerRef)}
			onResolve={handleResolve}
			onDelete={handleDelete}
			supportedTopLevelActions={supportedCommentActions}
			isOpeningMediaCommentFromToolbar={isOpeningMediaCommentFromToolbar}
			inlineCommentRef={inlineCommentRef}
			isInlineCommentVisible={isInlineCommentVisible}
			isOffline={isOffline}
			key={`inline-comment-${selectedAnnotation.id}`}
		/>
	);
};
